import { fg } from '@atlassian/jira-feature-gating';
import { createResource, useResource, getResourceStore } from '@atlassian/react-resource-router';
import { getAdminSettingsByKeys, getBannerSettings } from './services/index.tsx';
import {
	RESOURCE_ASSETS_NAVIGATION_SETTINGS,
	RESOURCE_ASSETS_NAVIGATION_SETTINGS_KEY,
	RESOURCE_ASSETS_BANNER_SETTINGS,
	RESOURCE_ASSETS_BANNER_SETTINGS_KEY,
	NAVIGATION_SETTINGS_KEYS,
	ASSETS_ADMIN_SETTINGS_MAX_AGE,
	emptyNavigationSettingsResponse,
	emptyBannerSettingsResponse,
} from './common/constants.tsx';
import { extractResourceStatePromise } from './common/utils.tsx';
import type { BannerSettings } from './common/types.tsx';

export interface AssetsNavigationSettingsResource {
	backgroundColor: string;
	highlightColor: string;
}

export const assetsNavigationSettingsResource = createResource<AssetsNavigationSettingsResource>({
	type: RESOURCE_ASSETS_NAVIGATION_SETTINGS,
	getKey: () => RESOURCE_ASSETS_NAVIGATION_SETTINGS_KEY,
	getData: async () => {
		if (!fg('assets-announcement-banner-settings')) {
			return emptyNavigationSettingsResponse;
		}

		const navigationSettingsRequest = [
			{ key: NAVIGATION_SETTINGS_KEYS.BG_COLOR },
			{ key: NAVIGATION_SETTINGS_KEYS.HIGHLIGHT_COLOR },
		];
		const { storeState } = getResourceStore();
		const state = storeState.getState();

		try {
			const workspaceId = await extractResourceStatePromise(state.data);

			if (!workspaceId || typeof workspaceId !== 'string') {
				return emptyNavigationSettingsResponse;
			}

			const navigationSettings = await getAdminSettingsByKeys(
				workspaceId,
				navigationSettingsRequest,
			);

			return {
				backgroundColor: navigationSettings[NAVIGATION_SETTINGS_KEYS.BG_COLOR]?.value,
				highlightColor: navigationSettings[NAVIGATION_SETTINGS_KEYS.HIGHLIGHT_COLOR]?.value,
			};
		} catch (error) {
			return emptyNavigationSettingsResponse;
		}
	},
	maxAge: ASSETS_ADMIN_SETTINGS_MAX_AGE,
});

export const useAssetsNavigationSettingsResource = () => {
	return useResource<AssetsNavigationSettingsResource>(assetsNavigationSettingsResource);
};

export const assetsBannerSettingsResource = createResource<BannerSettings>({
	type: RESOURCE_ASSETS_BANNER_SETTINGS,
	getKey: () => RESOURCE_ASSETS_BANNER_SETTINGS_KEY,
	getData: async () => {
		if (!fg('assets-announcement-banner-settings')) {
			return emptyBannerSettingsResponse;
		}

		const { storeState } = getResourceStore();
		const state = storeState.getState();

		try {
			const workspaceId = await extractResourceStatePromise(state.data);

			if (!workspaceId || typeof workspaceId !== 'string') {
				return emptyBannerSettingsResponse;
			}

			const bannerSettings = await getBannerSettings(workspaceId);

			return bannerSettings;
		} catch (error) {
			return emptyBannerSettingsResponse;
		}
	},
	maxAge: ASSETS_ADMIN_SETTINGS_MAX_AGE,
});

export const useAssetsBannerSettingsResource = () => {
	return useResource<BannerSettings>(assetsBannerSettingsResource);
};
